import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatRON } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Pencil, Trash2, Folder, X, ArrowUpDown, ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { toast } from "sonner";

const NO_CATEGORY = "__none__";

function stockBadgeClass(stock) {
  const n = Number(stock || 0);
  if (n <= 0) return "bg-red-50 text-red-700 border border-red-200";
  if (n < 10) return "bg-amber-50 text-amber-800 border border-amber-200";
  return "bg-emerald-50 text-emerald-800 border border-emerald-200";
}
const empty = {
  name: "",
  code: "",
  unit: "",
  purchase_price: "",
  markup: "",
  stock: "",
  category_id: NO_CATEGORY,
  width_m: "",
  colors: [],
};

export default function MaterialsPage() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [savingCat, setSavingCat] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [drag, setDrag] = useState(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [m, c] = await Promise.all([api.get("/materials"), api.get("/categories")]);
      setRows(m.data);
      setCategories(c.data);
    } catch (e) {
      if (!silent)
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Polling 3s. Pause when any dialog is open or tab hidden.
  useEffect(() => {
    if (open || catOpen || reorderMode) return;
    const tick = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, [open, catOpen, reorderMode]);

  const categoriesById = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = c;
    return m;
  }, [categories]);

  // Group materials by category preserving the custom order from the API;
  // uncategorised group is shown last.
  const grouped = useMemo(() => {
    const order = categories.map((c) => c.id);
    const buckets = {};
    for (const id of order) buckets[id] = [];
    buckets[NO_CATEGORY] = [];
    for (const m of rows) {
      const k = m.category_id && buckets[m.category_id] ? m.category_id : NO_CATEGORY;
      buckets[k].push(m);
    }
    const result = order
      .filter((id) => buckets[id].length > 0)
      .map((id) => ({ id, name: categoriesById[id]?.name || "—", items: buckets[id] }));
    if (buckets[NO_CATEGORY].length > 0) {
      result.push({ id: NO_CATEGORY, name: "Fara categorie", items: buckets[NO_CATEGORY] });
    }
    return result;
  }, [rows, categories, categoriesById]);

  const persistMaterials = async (flat) => {
    setRows(flat);
    try {
      await api.put("/materials/reorder", { ids: flat.map((m) => m.id) });
    } catch {
      toast.error("Eroare la salvarea ordinii materialelor");
    }
  };

  const persistCategories = async (next) => {
    setCategories(next);
    try {
      await api.put("/categories/reorder", { ids: next.map((c) => c.id) });
    } catch {
      toast.error("Eroare la salvarea ordinii categoriilor");
    }
  };

  const moveCategory = (catId, dir) => {
    const visible = grouped.filter((g) => g.id !== NO_CATEGORY).map((g) => g.id);
    const vi = visible.indexOf(catId);
    const vj = vi + dir;
    if (vi < 0 || vj < 0 || vj >= visible.length) return;
    const next = [...categories];
    const i = next.findIndex((c) => c.id === catId);
    const j = next.findIndex((c) => c.id === visible[vj]);
    [next[i], next[j]] = [next[j], next[i]];
    persistCategories(next);
  };

  const dropCategoryOn = (dragId, targetId) => {
    if (dragId === targetId) return;
    const next = [...categories];
    const from = next.findIndex((c) => c.id === dragId);
    if (from < 0) return;
    const [moved] = next.splice(from, 1);
    const to = next.findIndex((c) => c.id === targetId);
    if (to < 0) return;
    next.splice(to, 0, moved);
    persistCategories(next);
  };

  const moveMaterial = (groupIdx, itemIdx, dir) => {
    const g = grouped.map((gr) => ({ ...gr, items: [...gr.items] }));
    const items = g[groupIdx].items;
    const j = itemIdx + dir;
    if (j < 0 || j >= items.length) return;
    [items[itemIdx], items[j]] = [items[j], items[itemIdx]];
    persistMaterials(g.flatMap((gr) => gr.items));
  };

  const dropMaterialOn = (dragMatId, groupId, targetIdx) => {
    const g = grouped.map((gr) => ({ ...gr, items: [...gr.items] }));
    const grp = g.find((gr) => gr.id === groupId);
    if (!grp) return;
    const from = grp.items.findIndex((m) => m.id === dragMatId);
    if (from < 0 || from === targetIdx) return;
    const [moved] = grp.items.splice(from, 1);
    grp.items.splice(targetIdx, 0, moved);
    persistMaterials(g.flatMap((gr) => gr.items));
  };

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name,
      code: m.code || "",
      unit: m.unit || "",
      purchase_price: m.purchase_price,
      markup: m.markup,
      stock: m.stock ?? 0,
      category_id: m.category_id || NO_CATEGORY,
      width_m: m.width_m ?? "",
      colors: (m.colors || []).map((c) => ({
        id: (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) || `c-${Math.random()}`,
        name: c.name || "",
        purchase_price: c.purchase_price ?? "",
        code: c.code || "",
      })),
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanColors = (form.colors || [])
        .map((c) => ({
          name: (c.name || "").trim(),
          purchase_price: Number(c.purchase_price),
          code: (c.code || "").trim(),
        }))
        .filter((c) => c.name && !Number.isNaN(c.purchase_price) && c.purchase_price >= 0);
      const payload = {
        name: form.name.trim(),
        code: (form.code || "").trim(),
        unit: (form.unit || "").trim(),
        purchase_price: Number(form.purchase_price),
        markup: Number(form.markup),
        stock: Number(form.stock || 0),
        category_id: form.category_id === NO_CATEGORY ? null : form.category_id,
        width_m: Number(form.width_m || 0),
        colors: cleanColors,
      };
      if (!payload.name) {
        toast.error("Denumirea este obligatorie");
        return;
      }
      if (editing) {
        await api.put(`/materials/${editing.id}`, payload);
        toast.success("Material actualizat");
      } else {
        await api.post("/materials", payload);
        toast.success("Material adaugat");
      }
      setOpen(false);
      setForm(empty);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Sterg "${m.name}"?`)) return;
    try {
      await api.delete(`/materials/${m.id}`);
      toast.success("Material sters");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare la stergere");
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;
    setSavingCat(true);
    try {
      await api.post("/categories", { name: newCatName.trim() });
      setNewCatName("");
      toast.success("Categorie adaugata");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare");
    } finally {
      setSavingCat(false);
    }
  };

  const removeCategory = async (cat) => {
    const usage = rows.filter((r) => r.category_id === cat.id).length;
    const msg = usage
      ? `"${cat.name}" are ${usage} materiale. Acestea raman, dar fara categorie. Continui?`
      : `Sterg categoria "${cat.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/categories/${cat.id}`);
      toast.success("Categorie stearsa");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare");
    }
  };

  const renderRow = (m, group, groupIdx, itemIdx) => (
    <TableRow
      key={m.id}
      data-testid={`material-row-${m.id}`}
      draggable={reorderMode}
      onDragStart={(e) => {
        setDrag({ type: "mat", id: m.id, groupId: group.id });
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        if (drag?.type === "mat" && drag.groupId === group.id) e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (drag?.type === "mat" && drag.groupId === group.id && drag.id !== m.id) {
          dropMaterialOn(drag.id, group.id, itemIdx);
        }
        setDrag(null);
      }}
      className={
        reorderMode
          ? "cursor-grab active:cursor-grabbing " +
            (drag?.type === "mat" && drag.id === m.id ? "opacity-40" : "")
          : ""
      }
    >
      <TableCell className="text-black font-medium">
        {m.code && (
          <span className="mr-2 inline-block px-1.5 py-0 rounded-sm bg-black text-white font-mono text-[10px] align-middle">
            {m.code}
          </span>
        )}
        {m.name}
        {Number(m.width_m || 0) > 0 && (
          <span className="ml-2 inline-block px-1.5 py-0 rounded-sm bg-zinc-100 border border-zinc-200 font-mono text-[10px] text-zinc-700 align-middle">
            l={Number(m.width_m).toLocaleString("ro-RO")} m
          </span>
        )}
      </TableCell>
      <TableCell className="text-center text-zinc-700 font-mono text-xs">{m.unit || "—"}</TableCell>
      <TableCell className="text-right text-black font-mono">{formatRON(m.purchase_price)}</TableCell>
      <TableCell className="text-right text-black font-mono">
        {Number(m.markup).toFixed(2)} %
      </TableCell>
      <TableCell className="text-right text-black font-mono font-semibold">
        {formatRON(Number(m.purchase_price) * (1 + Number(m.markup) / 100))}
      </TableCell>
      <TableCell className="text-right">
        <span
          className={
            "inline-block rounded-full px-3 py-0.5 font-mono text-xs font-bold " +
            stockBadgeClass(m.stock)
          }
          data-testid={`material-stock-${m.id}`}
        >
          {Number(m.stock || 0).toLocaleString("ro-RO")}
        </span>
      </TableCell>
      <TableCell className="text-right">
        {reorderMode ? (
          <div className="flex justify-end items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => moveMaterial(groupIdx, itemIdx, -1)}
              disabled={itemIdx === 0}
              className="h-8 w-8 hover:bg-zinc-100 disabled:opacity-30"
              data-testid={`move-material-up-${m.id}`}
              title="Muta mai sus"
            >
              <ChevronUp size={16} className="text-black" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => moveMaterial(groupIdx, itemIdx, 1)}
              disabled={itemIdx === group.items.length - 1}
              className="h-8 w-8 hover:bg-zinc-100 disabled:opacity-30"
              data-testid={`move-material-down-${m.id}`}
              title="Muta mai jos"
            >
              <ChevronDown size={16} className="text-black" />
            </Button>
            <GripVertical size={15} className="text-zinc-400 ml-1" />
          </div>
        ) : (
          <div className="flex justify-end gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => openEdit(m)}
              className="h-8 w-8 hover:bg-zinc-100"
              data-testid={`edit-material-${m.id}`}
            >
              <Pencil size={15} className="text-black" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => remove(m)}
              className="h-8 w-8 hover:bg-red-50"
              data-testid={`delete-material-${m.id}`}
            >
              <Trash2 size={15} className="text-red-600" />
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="materials-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
            Inventar intern
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-black">
            Materiale
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">
            Gestioneaza lista materialelor, pretul de achizitie, adaosul procentual si stocul.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setReorderMode((v) => !v)}
            variant={reorderMode ? "default" : "outline"}
            className={
              "rounded-sm h-11 px-4 " +
              (reorderMode
                ? "bg-black hover:bg-zinc-800 text-white"
                : "border-zinc-300 text-black")
            }
            data-testid="toggle-reorder-button"
          >
            <ArrowUpDown size={16} className="mr-2" />
            {reorderMode ? "Gata" : "Reordonare"}
          </Button>
          <Button
            onClick={() => setCatOpen(true)}
            variant="outline"
            className="rounded-sm border-zinc-300 h-11 px-4 text-black"
            data-testid="open-categories-button"
          >
            <Folder size={16} className="mr-2" />
            Categorii ({categories.length})
          </Button>
          <Button
            onClick={openNew}
            className="rounded-sm bg-black hover:bg-zinc-800 text-white h-11 px-5"
            data-testid="add-material-button"
          >
            <Plus size={18} className="mr-2" />
            Adauga material
          </Button>
        </div>
      </div>

      {reorderMode && (
        <div
          className="mb-4 px-4 py-3 border border-amber-300 bg-amber-50 rounded-sm text-sm text-amber-900"
          data-testid="reorder-hint"
        >
          <span className="font-semibold">Mod reordonare activ.</span> Trage rândurile cu
          mouse-ul (drag &amp; drop) sau folosește săgețile ↑ ↓. Categoriile se mută între ele,
          materialele se mută în interiorul categoriei lor. Ordinea se salvează automat și se
          aplică și în Calculator / Deviz.
        </div>
      )}

      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="text-black font-semibold uppercase tracking-wide text-xs">
                Denumire material
              </TableHead>
              <TableHead className="text-black font-semibold uppercase tracking-wide text-xs text-center">
                U.M.
              </TableHead>
              <TableHead className="text-black font-semibold uppercase tracking-wide text-xs text-right">
                Pret achizitie
              </TableHead>
              <TableHead className="text-black font-semibold uppercase tracking-wide text-xs text-right">
                Adaos (%)
              </TableHead>
              <TableHead className="text-black font-semibold uppercase tracking-wide text-xs text-right">
                Pret vanzare
              </TableHead>
              <TableHead className="text-black font-semibold uppercase tracking-wide text-xs text-right">
                Stoc
              </TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-zinc-500">
                  Se incarca...
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-10 text-zinc-500"
                  data-testid="materials-empty"
                >
                  Niciun material inregistrat. Apasa "Adauga material".
                </TableCell>
              </TableRow>
            ) : (
              grouped.map((group, groupIdx) => (
                <React.Fragment key={group.id}>
                  <TableRow
                    className={
                      "bg-zinc-100/70 hover:bg-zinc-100/70" +
                      (reorderMode && group.id !== NO_CATEGORY
                        ? " cursor-grab active:cursor-grabbing" +
                          (drag?.type === "cat" && drag.id === group.id ? " opacity-40" : "")
                        : "")
                    }
                    draggable={reorderMode && group.id !== NO_CATEGORY}
                    onDragStart={(e) => {
                      setDrag({ type: "cat", id: group.id });
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      if (drag?.type === "cat" && group.id !== NO_CATEGORY) e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (drag?.type === "cat" && group.id !== NO_CATEGORY) {
                        dropCategoryOn(drag.id, group.id);
                      }
                      setDrag(null);
                    }}
                  >
                    <TableCell colSpan={7} className="py-2">
                      <div className="flex items-center gap-2">
                        <Folder size={14} className="text-zinc-700" />
                        <span className="font-mono uppercase text-[11px] tracking-widest text-zinc-700 font-bold">
                          {group.name}
                        </span>
                        <span className="font-mono text-[10px] text-zinc-500">
                          ({group.items.length})
                        </span>
                        {reorderMode && group.id !== NO_CATEGORY && (
                          <span className="ml-auto flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => moveCategory(group.id, -1)}
                              className="h-7 w-7 hover:bg-zinc-200"
                              data-testid={`move-category-up-${group.id}`}
                              title="Muta categoria mai sus"
                            >
                              <ChevronUp size={15} className="text-black" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => moveCategory(group.id, 1)}
                              className="h-7 w-7 hover:bg-zinc-200"
                              data-testid={`move-category-down-${group.id}`}
                              title="Muta categoria mai jos"
                            >
                              <ChevronDown size={15} className="text-black" />
                            </Button>
                            <GripVertical size={14} className="text-zinc-400" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {group.items.map((m, itemIdx) => renderRow(m, group, groupIdx, itemIdx))}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* ----- Material dialog ----- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-md rounded-sm max-h-[90vh] overflow-y-auto"
          data-testid="material-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight text-black">
              {editing ? "Editeaza material" : "Material nou"}
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              Completeaza datele materialului. Categoria si latimea sunt optionale.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label
                htmlFor="m-name"
                className="text-black font-semibold uppercase text-xs tracking-wide"
              >
                Denumire material
              </Label>
              <Input
                id="m-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="mt-2 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="material-name-input"
              />
            </div>

            <div>
              <Label
                htmlFor="m-code"
                className="text-black font-semibold uppercase text-xs tracking-wide"
              >
                Cod material (optional)
              </Label>
              <Input
                id="m-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="ex: TBL001"
                className="mt-2 rounded-sm border-zinc-300 focus-visible:ring-black font-mono"
                data-testid="material-code-input"
              />
              <div className="text-[11px] text-zinc-500 mt-1">
                Util pentru cautare rapida. Apare langa numele materialului.
              </div>
            </div>

            <div>
              <Label className="text-black font-semibold uppercase text-xs tracking-wide">
                Categorie
              </Label>
              <Select
                value={form.category_id}
                onValueChange={(v) => setForm({ ...form, category_id: v })}
              >
                <SelectTrigger
                  className="mt-2 rounded-sm border-zinc-300 focus:ring-black"
                  data-testid="material-category-select"
                >
                  <SelectValue placeholder="Selecteaza categorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CATEGORY}>Fara categorie</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-zinc-500 mt-1">
                Gestioneaza lista din butonul "Categorii".
              </div>
            </div>

            <div>
              <Label
                htmlFor="m-unit"
                className="text-black font-semibold uppercase text-xs tracking-wide"
              >
                Unitate de masura
              </Label>
              <Input
                id="m-unit"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="mt-2 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="material-unit-input"
                placeholder="ex: buc, mp, ml, kg, l"
                maxLength={12}
              />
            </div>

            <div>
              <Label
                htmlFor="m-width"
                className="text-black font-semibold uppercase text-xs tracking-wide"
              >
                Latime standard (m)
              </Label>
              <Input
                id="m-width"
                type="number"
                step="0.001"
                min="0"
                value={form.width_m}
                onChange={(e) => setForm({ ...form, width_m: e.target.value })}
                className="mt-2 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="material-width-input"
                placeholder="0"
              />
              <div className="text-xs text-zinc-500 mt-1">
                Optional. Daca e completata (&gt; 0), in Calculator/Deviz introduci doar lungimea
                in metri si cantitatea (m²) se calculeaza automat.
              </div>
            </div>

            <div>
              <Label
                htmlFor="m-price"
                className="text-black font-semibold uppercase text-xs tracking-wide"
              >
                Pret achizitie (RON)
              </Label>
              <Input
                id="m-price"
                type="number"
                step="0.01"
                min="0"
                value={form.purchase_price}
                onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
                required
                className="mt-2 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="material-price-input"
              />
            </div>

            <div>
              <Label
                htmlFor="m-markup"
                className="text-black font-semibold uppercase text-xs tracking-wide"
              >
                Adaos comercial (%)
              </Label>
              <Input
                id="m-markup"
                type="number"
                step="0.01"
                min="0"
                max="10000"
                value={form.markup}
                onChange={(e) => setForm({ ...form, markup: e.target.value })}
                required
                className="mt-2 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="material-markup-input"
              />
            </div>

            <div>
              <Label
                htmlFor="m-stock"
                className="text-black font-semibold uppercase text-xs tracking-wide"
              >
                Stoc (cantitate)
              </Label>
              <Input
                id="m-stock"
                type="number"
                step="0.01"
                min="0"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="mt-2 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="material-stock-input"
                placeholder="0"
              />
              <div className="text-xs text-zinc-500 mt-1">
                Scade automat la facturare. Stocul 0 nu sterge materialul.
              </div>
            </div>

            {/* ----- Colors block (optional, multiple) ----- */}
            <div className="pt-3 border-t border-dashed border-zinc-200">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-black font-semibold uppercase text-xs tracking-wide">
                  Culori (optional)
                </Label>
                <button
                  type="button"
                  onClick={() => {
                    const id = (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) || `c-${Math.random()}`;
                    setForm({ ...form, colors: [...(form.colors || []), { id, name: "", purchase_price: "", code: "" }] });
                  }}
                  className="px-2 py-1 rounded-sm border border-zinc-300 bg-white text-black hover:border-zinc-500 font-mono uppercase text-[10px] tracking-wider"
                  data-testid="material-color-add"
                >
                  + Adauga culoare
                </button>
              </div>
              {(form.colors || []).length === 0 ? (
                <div className="text-[11px] text-zinc-500 font-mono italic">
                  Culoarea de baza se numeste "Mat" si foloseste pretul de achizitie de mai sus.
                  Adauga culori suplimentare (ex: Quart) daca au pret de achizitie diferit.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-[10px] text-zinc-500 font-mono">
                    Culoarea de baza "Mat" = pretul si codul de mai sus. Mai jos culorile cu pret si cod propriu (codul gol = codul de baza).
                  </div>
                  {form.colors.map((c, idx) => (
                    <div key={c.id} className="flex items-center gap-2" data-testid={`material-color-row-${idx}`}>
                      <Input
                        value={c.name}
                        onChange={(e) => {
                          const next = [...form.colors];
                          next[idx] = { ...next[idx], name: e.target.value };
                          setForm({ ...form, colors: next });
                        }}
                        placeholder="Quart / Verde / RAL 7016..."
                        className="rounded-sm border-zinc-300 focus-visible:ring-black h-9 text-sm"
                        data-testid={`material-color-name-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={c.purchase_price}
                        onChange={(e) => {
                          const next = [...form.colors];
                          next[idx] = { ...next[idx], purchase_price: e.target.value };
                          setForm({ ...form, colors: next });
                        }}
                        placeholder="pret"
                        className="rounded-sm border-zinc-300 focus-visible:ring-black h-9 w-24 shrink-0 text-sm font-mono"
                        data-testid={`material-color-price-${idx}`}
                      />
                      <Input
                        value={c.code}
                        onChange={(e) => {
                          const next = [...form.colors];
                          next[idx] = { ...next[idx], code: e.target.value };
                          setForm({ ...form, colors: next });
                        }}
                        placeholder="cod culoare"
                        className="rounded-sm border-zinc-300 focus-visible:ring-black h-9 w-24 shrink-0 text-sm font-mono"
                        data-testid={`material-color-code-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = form.colors.filter((_, i) => i !== idx);
                          setForm({ ...form, colors: next });
                        }}
                        className="text-red-500 hover:text-red-700 p-1.5"
                        data-testid={`material-color-remove-${idx}`}
                        title="Sterge"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                    Adaosul ({form.markup || 0}%) se aplica si pe culori. Pret vanzare = pret achizitie culoare × (1 + adaos%).
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-sm border-zinc-300"
                data-testid="material-cancel"
              >
                Anuleaza
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-sm bg-black hover:bg-zinc-800 text-white"
                data-testid="material-save"
              >
                {saving ? "Se salveaza..." : "Salveaza"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ----- Categories dialog ----- */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="categories-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight text-black">
              Categorii
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              Adauga categorii pentru a grupa materialele (ex: Sisteme pluviale, Tigle).
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={addCategory} className="flex gap-2">
            <Input
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="ex: Sisteme pluviale"
              maxLength={60}
              className="rounded-sm border-zinc-300 focus-visible:ring-black"
              data-testid="new-category-input"
            />
            <Button
              type="submit"
              disabled={savingCat || !newCatName.trim()}
              className="rounded-sm bg-black hover:bg-zinc-800 text-white"
              data-testid="add-category-submit"
            >
              <Plus size={16} className="mr-1" />
              Adauga
            </Button>
          </form>

          <div className="max-h-[280px] overflow-y-auto border border-zinc-200 rounded-sm">
            {categories.length === 0 ? (
              <div className="text-center text-zinc-500 text-sm py-6">
                Nicio categorie. Adauga una mai sus.
              </div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {categories.map((c) => {
                  const count = rows.filter((r) => r.category_id === c.id).length;
                  return (
                    <li
                      key={c.id}
                      className="flex items-center justify-between px-3 py-2"
                      data-testid={`category-row-${c.id}`}
                    >
                      <div>
                        <div className="text-sm font-medium text-black">{c.name}</div>
                        <div className="text-[10px] font-mono text-zinc-500">
                          {count} {count === 1 ? "material" : "materiale"}
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeCategory(c)}
                        className="h-8 w-8 hover:bg-red-50"
                        data-testid={`delete-category-${c.id}`}
                      >
                        <X size={14} className="text-red-600" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCatOpen(false)}
              className="rounded-sm border-zinc-300"
              data-testid="categories-close"
            >
              Inchide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
